import { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { API_URL } from "@/lib/config";

function getToken(): string | null {
  return localStorage.getItem("access_token");
}

/**
 * Subscribe to real-time events for all incidents in the current project.
 * Invalidates the incidents list cache when any incident is created, updated, or deleted.
 */
export function useProjectIncidentEvents() {
  const queryClient = useQueryClient();
  const { projectId } = useParams<{ projectId: string }>();
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!projectId) return;

    const token = getToken();
    if (!token) return;

    const url = `${API_URL}/api/v1/projects/${projectId}/incidents/events?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    esRef.current = es;

    const invalidate = () => {
      queryClient.invalidateQueries({ queryKey: ["incidents", projectId] });
      queryClient.invalidateQueries({ queryKey: ["project-stats", projectId] });
    };

    es.addEventListener("incident-created", invalidate);
    es.addEventListener("incident-updated", invalidate);
    es.addEventListener("incident-deleted", invalidate);

    return () => {
      es.close();
      esRef.current = null;
    };
  }, [projectId, queryClient]);
}

/**
 * Subscribe to real-time incident events (comments, status changes).
 * Invalidates relevant React Query caches when new data arrives.
 */
export function useIncidentEvents(incidentId: string | undefined) {
  const queryClient = useQueryClient();
  const { projectId } = useParams<{ projectId: string }>();
  const esRef = useRef<EventSource | null>(null);

  useEffect(() => {
    if (!incidentId || !projectId) return;

    const token = getToken();
    if (!token) return;

    const url = `${API_URL}/api/v1/projects/${projectId}/incidents/${incidentId}/events?token=${encodeURIComponent(token)}`;
    const es = new EventSource(url);
    esRef.current = es;

    es.addEventListener("comment-added", () => {
      queryClient.invalidateQueries({
        queryKey: ["incident", incidentId, "comments"],
      });
    });

    es.addEventListener("incident-updated", () => {
      queryClient.invalidateQueries({ queryKey: ["incident", incidentId] });
      queryClient.invalidateQueries({ queryKey: ["incidents"] });
    });

    return () => {
      es.close();
      esRef.current = null;
    };
  }, [incidentId, projectId, queryClient]);
}
